import { useOverlay } from "@base/model/hooks/useOverlay";
import { Button } from "@base/ui/Button/Button";
import { Dialog } from "@base/ui/Dialog";
import { membershipQueries } from "@features/membership/api/queryFactory";
import { createUnsubscriptionRequest } from "@features/membership/api/unsubscription";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import UnscribeCompleteDialog from "./UnscribeCompleteDialog";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const UnscribeMembershipDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { open } = useOverlay();

  const handleOpenUnsubscriptionConfirmDialog = () => {
    open(({ isOpen, close }) => (
      <UnscribeCompleteDialog
        isOpen={isOpen}
        onClose={() => {
          close();
          queryClient.invalidateQueries({ queryKey: membershipQueries.all() });
          navigate({ to: "/" });
        }}
      />
    ));
  };

  const { mutate, isPending } = useMutation({
    mutationFn: createUnsubscriptionRequest,
    onSuccess: () => {
      handleOpenUnsubscriptionConfirmDialog();
      onClose();
    },
    onError: () => {
      toast.error(t("unsubscriptionFailed"));
    },
  });

  const handleUnscribe = () => {
    if (isPending) return;

    mutate();
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Dialog.Content className="w-[400px]">
        <div className="flex flex-col gap-4 p-4 pt-6">
          <Dialog.Title>{t("unsubscribeMembershipTitle")}</Dialog.Title>
          <Dialog.Description className="whitespace-pre-line break-keep">
            {t("unsubscribeMembershipDescription")}
          </Dialog.Description>
          <div className="flex flex-col gap-1 mt-2">
            <Button
              type="button"
              variant="fill"
              color="danger"
              className="w-full"
              onClick={handleUnscribe}
              disabled={isPending}
            >
              {t("unsubscribe")}
            </Button>
            <Button type="button" variant="ghost" color="secondary" className="w-full" onClick={onClose}>
              {t("cancel")}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default UnscribeMembershipDialog;
